"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { LogOutIcon } from "lucide-react"
import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { accountNavigationItems } from "./account-navigation"

interface AccountSidebarUser {
  name?: string
  email?: string
}

interface AccountSidebarProps {
  user: AccountSidebarUser | null
}

interface AuthMeResponse {
  user: {
    name?: string
  } | null
}

interface CartItemsResponse {
  ok: boolean
  cartCount: number
  items: unknown[]
  totals: {
    subtotal: number
    total: number
    currency: string
  }
}

const AUTH_ME_QUERY_KEY = ["auth", "me"] as const
const CART_ITEMS_QUERY_KEY = ["cart", "items"] as const
const DEFAULT_ACCOUNT_NAME = "Cliente"
const GREETING = "Ola!"
const EMPTY_CART: CartItemsResponse = {
  ok: true,
  cartCount: 0,
  items: [],
  totals: {
    subtotal: 0,
    total: 0,
    currency: "BRL",
  },
}

const toAccountDisplayName = (user: AccountSidebarUser | null): string => {
  const name = user?.name?.trim()

  if (name) {
    return name
  }

  const email = user?.email?.trim()

  if (email) {
    return email
  }

  return DEFAULT_ACCOUNT_NAME
}

const toAvatarFallback = (value: string): string => {
  const parts = value.trim().replace(/\s+/g, " ").split(" ").filter(Boolean)

  if (parts.length === 0) {
    return "CL"
  }

  const firstPart = parts[0] ?? ""
  const lastPart = parts.at(-1) ?? ""
  const firstChar = firstPart[0] ?? ""
  const secondChar =
    parts.length > 1 ? (lastPart[0] ?? "") : (firstPart[1] ?? "")

  const fallback = `${firstChar}${secondChar}`.toUpperCase()

  return fallback || "CL"
}

const isActiveRoute = (pathname: string, href: string) => {
  if (href === "/minha-conta") {
    return pathname === href
  }

  return pathname.startsWith(href)
}

export function AccountSidebar({ user }: AccountSidebarProps) {
  const router = useRouter()
  const pathname = usePathname()
  const queryClient = useQueryClient()
  const displayName = toAccountDisplayName(user)
  const avatarFallback = toAvatarFallback(displayName)

  const logout = useMutation({
    mutationFn: () => axios.post("/api/auth/logout"),
    onMutate: () => {
      const previousAuthMe =
        queryClient.getQueryData<AuthMeResponse>(AUTH_ME_QUERY_KEY)
      const previousCartItems =
        queryClient.getQueryData<CartItemsResponse>(CART_ITEMS_QUERY_KEY)

      queryClient.setQueryData<AuthMeResponse>(AUTH_ME_QUERY_KEY, {
        user: null,
      })
      queryClient.setQueryData<CartItemsResponse>(
        CART_ITEMS_QUERY_KEY,
        EMPTY_CART
      )

      return { previousAuthMe, previousCartItems }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousAuthMe) {
        queryClient.setQueryData<AuthMeResponse>(
          AUTH_ME_QUERY_KEY,
          context.previousAuthMe
        )
      }

      if (context?.previousCartItems) {
        queryClient.setQueryData<CartItemsResponse>(
          CART_ITEMS_QUERY_KEY,
          context.previousCartItems
        )
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: AUTH_ME_QUERY_KEY,
        refetchType: "all",
      })
      await queryClient.invalidateQueries({
        queryKey: CART_ITEMS_QUERY_KEY,
        refetchType: "all",
      })

      router.replace("/")
    },
  })

  return (
    <aside className='space-y-5'>
      <div className='rounded-2xl border bg-white p-4 shadow-xs'>
        <div className='flex items-center gap-3'>
          <Avatar className='size-14 border'>
            <AvatarFallback>{avatarFallback}</AvatarFallback>
          </Avatar>

          <div className='min-w-0'>
            <p className='text-muted-foreground text-sm'>{GREETING}</p>
            <p className='truncate font-semibold text-base'>{displayName}</p>
          </div>
        </div>
      </div>

      <nav
        aria-label='Navegacao da conta'
        className='overflow-hidden rounded-2xl border bg-white shadow-xs'
      >
        <ul>
          {accountNavigationItems.map((item) => {
            const isActive = isActiveRoute(pathname, item.href)
            const Icon = item.icon

            return (
              <li
                className='border-border border-b last:border-b-0'
                key={item.href}
              >
                <Link
                  className={`flex items-center gap-3 px-4 py-4 font-semibold text-sm uppercase tracking-[0.08em] transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "bg-white text-foreground hover:bg-muted"
                  }`}
                  href={item.href}
                >
                  <Icon aria-hidden='true' size={16} />
                  <span>{item.label}</span>
                </Link>
              </li>
            )
          })}

          <li className='border-border border-b last:border-b-0'>
            <button
              className='flex w-full items-center gap-3 px-4 py-4 font-semibold text-destructive text-sm uppercase tracking-[0.08em] transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60'
              disabled={logout.isPending}
              onClick={() => logout.mutate()}
              type='button'
            >
              <LogOutIcon aria-hidden='true' size={16} />
              <span>{logout.isPending ? "Saindo..." : "Sair"}</span>
            </button>
          </li>
        </ul>
      </nav>
    </aside>
  )
}
